import { useEffect, useState } from "react";
import { nav, person } from "../data/site";

const STORAGE_KEY = "theme";

// Dark is the design, not a system preference. Light is there for whoever
// wants it, and the choice sticks once it is made.
function initialTheme() {
  const saved = typeof localStorage !== "undefined" && localStorage.getItem(STORAGE_KEY);
  return saved === "light" || saved === "dark" ? saved : "dark";
}

export default function Nav() {
  const [theme, setTheme] = useState(initialTheme);
  const [active, setActive] = useState("");
  const [open, setOpen] = useState(false);
  const [lifted, setLifted] = useState(false);

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem(STORAGE_KEY, theme);
  }, [theme]);

  // Active chapter. The nav list doubles as the page index, so there is no
  // separate progress rail to keep in sync.
  useEffect(() => {
    const sections = nav
      .map(({ id }) => document.getElementById(id))
      .filter(Boolean);

    // Track what is actually in the band, so leaving the last section clears the
    // marker instead of leaving it stuck on whatever was seen last.
    const visible = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) visible.add(entry.target.id);
          else visible.delete(entry.target.id);
        });
        setActive(nav.find(({ id }) => visible.has(id))?.id || "");
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );

    sections.forEach((section) => observer.observe(section));
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const onScroll = () => setLifted(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // A menu that stays open behind a scrolled page is a trap on a phone.
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Escape closes it, and so does widening the window past the breakpoint:
  // otherwise a rotated phone keeps a locked, invisible-menu page.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => event.key === "Escape" && setOpen(false);
    const wide = window.matchMedia("(min-width: 721px)");
    const onWide = () => wide.matches && setOpen(false);
    window.addEventListener("keydown", onKey);
    wide.addEventListener("change", onWide);
    return () => {
      window.removeEventListener("keydown", onKey);
      wide.removeEventListener("change", onWide);
    };
  }, [open]);

  // The sheet is a sibling of the header, not a child. The lifted header uses
  // backdrop-filter, which makes it the containing block for position:fixed
  // descendants, so a sheet inside it was sized to the 72px header instead of
  // the viewport and collapsed.
  return (
    <>
    <header className={`nav ${lifted ? "is-lifted" : ""} ${open ? "is-open" : ""}`}>
      <div className="nav__inner shell">
        <a href="#top" className="nav__name" onClick={() => setOpen(false)}>
          {person.short}
          <span className="nav__dot" aria-hidden="true" />
        </a>

        <nav className="nav__links" aria-label="Sections">
          {nav.map(({ id, label }) => (
            <a
              key={id}
              href={`#${id}`}
              className={`nav__link ${active === id ? "is-active" : ""}`}
              aria-current={active === id ? "true" : undefined}
            >
              {label}
            </a>
          ))}
        </nav>

        <div className="nav__actions">
          <button
            type="button"
            className="nav__theme"
            onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
            aria-label={`Switch to ${theme === "dark" ? "light" : "dark"} theme`}
          >
            <span className="nav__theme-track" data-theme-state={theme} />
            <span className="label">{theme === "dark" ? "Dark" : "Light"}</span>
          </button>

          <button
            type="button"
            className="nav__burger"
            onClick={() => setOpen((v) => !v)}
            aria-expanded={open}
            aria-controls="mobile-menu"
          >
            {open ? "Close" : "Menu"}
          </button>
        </div>
      </div>
    </header>

    <div
      id="mobile-menu"
      className={`nav__sheet ${open ? "is-open" : ""}`}
      aria-hidden={!open}
      inert={open ? undefined : ""}
    >
      {nav.map(({ id, label }, i) => (
        <a
          key={id}
          href={`#${id}`}
          onClick={() => setOpen(false)}
          style={{ transitionDelay: `${60 + i * 45}ms` }}
        >
          {label}
        </a>
      ))}
    </div>
    </>
  );
}
